import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  OnInit,
  OnDestroy,
  AfterViewInit,
} from "@angular/core";
import { EvaluationService } from "src/app/shared/services/evaluation.service";
import { MmpConfirmPopupComponent } from "../mmp-confirm-popup/mmp-confirm-popup.component";
import { MatDialog } from "@angular/material/dialog";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import { MatTabGroup, MatTabsModule } from "@angular/material/tabs";
import { Subject, takeUntil } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MmpAiSummaryComponent } from "../mmp-ai-summary/mmp-ai-summary.component";
import { MmpFeedbackComponent } from "../mmp-feedback/mmp-feedback.component";
import { TitleCasePipe } from "src/app/pipes/title-case.pipe";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import { AppConfigService } from "src/app/services/app-config.service";
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  selector: "app-mmp-evaluation",
  templateUrl: "./mmp-evaluation.component.html",
  styleUrls: ["./mmp-evaluation.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatTooltipModule,
    MmpAiSummaryComponent,
    MmpFeedbackComponent,
    TitleCasePipe,
  ],
})
export class MmpEvaluationComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private readonly destroy$ = new Subject<void>();
  currentStage: string = "Evaluation";
  progressTagMsg: string = "In progress";
  aiSummaryCourseCount: number;
  transferForCredit: number;
  notTransferForCredit: number;
  aiSummaryConfidenceScore: number;
  evaluationResult: any;
  isTransferToggleChecked: boolean;
  showConsoleLogs: boolean = false;

  activeAccordion: number | null = null;
  progressTimeText: string = "Evaluating...";
  progressTime!: string;
  ComparisonNormalizer = ComparisonNormalizer;

  @Output() evaluationComplete = new EventEmitter<void>();
  @Input() selectedTranscriptIndex!: number;
  @Output() selectedTranscriptIndexChange = new EventEmitter<number>();
  @Output() backClick = new EventEmitter<string>();
  @Input() resizeComplete!: EventEmitter<void>;
  @ViewChild(MatTabGroup) tabGroup!: MatTabGroup;
  @Output() disableTabs = new EventEmitter<string>();
  @Output() inactiveTranscriptsIndexesChange = new EventEmitter<number[]>();

  constructor(
    private readonly evaluationService: EvaluationService,
    private readonly confirmPopup: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly envService: AppConfigService,
  ) {
    this.showConsoleLogs = this.envService.showConsoleLogs;
  }

  ngOnInit(): void {
    const savedData = this.evaluationService.getEvaluationData();
    this.evaluationResult = this.normalizeEvaluationData(savedData);

    this.evaluationService.evaluationResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe((transcripts) => {
        const parsedTranscripts = this.parseCompletedTranscripts(transcripts);
        if (parsedTranscripts.length === 0) {
          return;
        }

        const mergedTranscripts = this.mergeTranscriptsBySessionKey(
          this.evaluationResult?.transcripts || [],
          parsedTranscripts,
        );

        this.evaluationResult = {
          transcripts: this.decorateTranscriptsForView(mergedTranscripts),
        };
        this.evaluationService.setEvaluationData(this.evaluationResult);
      });

    this.evaluationService.evaluationSummary$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data) {
          this.aiSummaryCourseCount = data.ai_summary.total_courses;
          this.transferForCredit = data.ai_summary.transfer_for_credit;
          this.notTransferForCredit = data.ai_summary.not_transfer_for_credit;
          this.aiSummaryConfidenceScore = data.ai_summary.confidence_score;
          this.progressTime = data.ai_summary.total_processing_time;
          this.progressTimeText = "Processing Time:";
          this.progressTagMsg = "Completed";
        }
      });

    this.evaluationResult = {
      transcripts: this.decorateTranscriptsForView(
        this.evaluationResult?.transcripts || [],
      ),
    };

    if (this.showConsoleLogs) {
      console.log("evaluation result new", this.evaluationResult);
    }
  }

  private normalizeEvaluationData(data: any): any {
    return {
      transcripts: this.decorateTranscriptsForView(data?.transcripts || []),
    };
  }

  private parseCompletedTranscripts(transcripts: any[]): any[] {
    const completed = (transcripts || []).filter(
      (t) => ComparisonNormalizer.string(t.status) === "completed",
    );

    return completed
      .map((t) => {
        let parsed: any = {};
        try {
          parsed = JSON.parse(t.output);
        } catch (e) {
          if (this.showConsoleLogs) {
            console.log("Error parsing transcript output", e);
          }
          parsed = t.output;
        }

        if (!parsed?.result) {
          return null;
        }

        if (
          !parsed.result.pre_evaluation_passed &&
          parsed.result.pre_evaluation_passed !== "false"
        ) {
          return {
            name: parsed.name,
            university: parsed.university,
            transcript_rule_evaluation:
              parsed.result.transcript_rule_evaluation || [],
            session_key: parsed.session_key,
          };
        }
        return null;
      })
      .filter((t) => t !== null);
  }

  private mergeTranscriptsBySessionKey(
    existingTranscripts: any[],
    incomingTranscripts: any[],
  ): any[] {
    const normalizeSessionKey = (sessionKey: unknown): string =>
      ComparisonNormalizer.string(sessionKey).trim();

    const existingMap = new Map<string, any>();
    (existingTranscripts || []).forEach((transcript: any) => {
      const normalizedKey = normalizeSessionKey(transcript?.session_key);
      if (normalizedKey !== "") {
        existingMap.set(normalizedKey, transcript);
      }
    });

    const mergedInIncomingOrder = incomingTranscripts.map((incoming: any) => {
      const normalizedIncomingKey = normalizeSessionKey(incoming?.session_key);
      let existing;

      if (normalizedIncomingKey === "") {
        existing = undefined;
      } else {
        existing = existingMap.get(normalizedIncomingKey);
      }
      if (!existing) {
        return incoming;
      }

      return {
        ...incoming,
        transcript_rule_evaluation: this.mergeCourseSelections(
          existing.transcript_rule_evaluation || [],
          incoming.transcript_rule_evaluation || [],
        ),
      };
    });

    const incomingKeys = new Set(
      incomingTranscripts
        .map((transcript: any) => normalizeSessionKey(transcript?.session_key))
        .filter((sessionKey: string) => sessionKey !== ""),
    );
    const existingOnly = (existingTranscripts || []).filter(
      (transcript: any) =>
        !incomingKeys.has(normalizeSessionKey(transcript?.session_key)),
    );

    return [...mergedInIncomingOrder, ...existingOnly];
  }

  private mergeCourseSelections(
    existingCourses: any[],
    incomingCourses: any[],
  ): any[] {
    return incomingCourses.map((course: any, index: number) => {
      const existing = existingCourses[index] || {};
      return {
        ...course,
        selected: existing.selected ?? course.selected,
        origin: existing.origin ?? course.origin ?? index,
        showRules: existing.showRules ?? false,
      };
    });
  }

  private decorateTranscriptsForView(transcripts: any[]): any[] {
    return (transcripts || []).map((transcript: any) => ({
      ...transcript,
      transcript_rule_evaluation: (
        transcript.transcript_rule_evaluation || []
      ).map((course: any, index: number) => ({
        ...course,
        origin: course.origin ?? index,
        showRules: course.showRules ?? false,
      })),
    }));
  }

  ngAfterViewInit(): void {
    // Listen for the resizeComplete event
    if (this.resizeComplete) {
      this.resizeComplete.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.tabGroup?._tabHeader) {
          this.tabGroup._tabHeader.updatePagination();
        }
      });
    }
  }

  toggleAccordion(index: number): void {
    this.activeAccordion = this.activeAccordion === index ? null : index;
  }

  onCourseCheckboxChange(
    transcriptIndex: number,
    courseIndex: number,
    event: Event,
  ): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      let dialogRef = this.confirmPopup.open(MmpConfirmPopupComponent, {
        width: "512px",
        data: {
          title: "Mark for Transfer Credit?",
          message: MESSAGES.EVALUATION.SELECT_COURSE,
          cancelText: "Cancel",
          confirmText: "Mark for Transfer",
        },
      });
      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          if (ComparisonNormalizer.string(result) === "yes") {
            this.disableTabs.emit(this.currentStage.toLowerCase());
            this.evaluationResult.transcripts[
              transcriptIndex
            ].transcript_rule_evaluation[courseIndex].selected =
              checkbox.checked;
            this.evaluationService.setEvaluationData(this.evaluationResult);
          } else {
            checkbox.checked = false;
          }
        });
    } else {
      let dialogRef = this.confirmPopup.open(MmpConfirmPopupComponent, {
        width: "512px",
        data: {
          title: "Remove from Transfer Credit?",
          message: MESSAGES.EVALUATION.REMOVE_COURSE,
          cancelText: "Cancel",
          confirmText: "Yes, Remove",
        },
      });
      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          if (ComparisonNormalizer.string(result) === "yes") {
            this.disableTabs.emit(this.currentStage.toLowerCase());
            this.evaluationResult.transcripts[
              transcriptIndex
            ].transcript_rule_evaluation[courseIndex].selected =
              checkbox.checked;
            this.evaluationService.setEvaluationData(this.evaluationResult);
          } else {
            checkbox.checked = true;
          }
        });
    }
  }

  getFilteredCourses(transcript: any) {
    // If toggle is checked, filter selected courses and preserve original indices
    if (this.isTransferToggleChecked) {
      return transcript.transcript_rule_evaluation.filter(
        (course: any) => course.selected,
      );
    }

    // If toggle is not checked, return the original array with added origin property
    return transcript.transcript_rule_evaluation;
  }

  preventAccordionToggle(event: Event): void {
    event.stopPropagation();
  }

  // Calculate selected courses for a transcript, or across all transcripts when omitted
  getSelectedCoursesCount(transcript?: any): number {
    const getSelectedCount = (currentTranscript: any): number =>
      (currentTranscript?.transcript_rule_evaluation || []).filter(
        (course: any) => course.selected,
      ).length;

    if (transcript) {
      return getSelectedCount(transcript);
    }

    return (this.evaluationResult?.transcripts || []).reduce(
      (total: number, currentTranscript: any) =>
        total + getSelectedCount(currentTranscript),
      0,
    );
  }

  // Calculate the total number of courses for a transcript
  getTotalCoursesCount(transcript: any): number {
    return transcript.transcript_rule_evaluation.length;
  }

  onTranscriptTabChange(event) {
    this.activeAccordion = null;
  }

   onTranscriptTabClick(event) {
    setTimeout(() => {
      this.selectedTranscriptIndexChange.emit(this.selectedTranscriptIndex);
    }, 10);
  }

  toggleRulesVisibility(course: any): void {
    course.showRules = !course.showRules;
  }

  handleSaveAndProceedClick(event: Event): void {
    if (this.getSelectedCoursesCount() === 0) {
      event.preventDefault();
      this.showToast(
        "toast-error",
        "No courses selected. Please select at least one course to continue",
      );
      return;
    }

    if (this.progressTagMsg === "Completed") {
      this.onSubmitEvaluation();
      return;
    }

    event.preventDefault();
    this.showToast(
      "toast-error",
      "Evaluation is still in progress. Please wait until it is completed to continue",
    );
  }

  onSubmitEvaluation() {
    const transcriptsWithoutSelections =
      this.evaluationResult.transcripts.filter(
        (transcript: any) => this.getSelectedCoursesCount(transcript) === 0,
      );
    const hasFalse = transcriptsWithoutSelections.length > 0;

    this.evaluationResult.transcripts.forEach((transcript: any) => {
      transcript.eligibleForProceeding =
        this.getSelectedCoursesCount(transcript) > 0;
    });

    const transcriptsWithoutSelectionsSet = new Set(transcriptsWithoutSelections);
    const inactiveIndexes = this.evaluationResult.transcripts
      .map((transcript: any, index: number) =>
        transcriptsWithoutSelectionsSet.has(transcript) ? index : -1,
      )
      .filter((index: number) => index !== -1);
    this.inactiveTranscriptsIndexesChange.emit(inactiveIndexes);

    this.evaluationService.setEvaluationData(this.evaluationResult);

    if (hasFalse) {
      const transcriptNames = transcriptsWithoutSelections
        .map(
          (transcript) => transcript.name || transcript.university || "Unknown",
        )
        .join(", ");

      const dialogRef = this.confirmPopup.open(MmpConfirmPopupComponent, {
        width: "608px",
        data: {
          title: "Confirm To Proceed",
          message: MESSAGES.EVALUATION.CONTINUE_WARNING,
          issues: [
            {
              title: "Transcripts without selected courses",
              description: `Transcripts: ${transcriptNames}`,
            },
          ],
          cancelText: "Review Transcripts",
          confirmText: "Continue to Equivalency",
        },
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          if (ComparisonNormalizer.string(result) === "yes") {
            this.evaluationCompleted();
          }
        });
    } else {
      this.evaluationCompleted();
    }
  }

  evaluationCompleted() {
    this.evaluationResult.transcripts.forEach((transcript) => {
      this.evaluationService.updateEvaluationOutput(
        transcript.session_key,
        transcript.transcript_rule_evaluation,
        transcript.eligibleForProceeding
      );
    });
    this.evaluationComplete.emit();
  }

  goToExtractionTab() {
    this.backClick.emit("extraction");
  }

  showToast(type: string, message: string) {
    this.snackBar.open(message, "x", {
      duration: 3000,
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: [type],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
